import React, { useState, useMemo, useEffect } from "react";
import PropTypes from 'prop-types';
import { Box, Button, Dialog, Grid, IconButton, Typography, useMediaQuery, useTheme, CircularProgress, Tabs, Tab, Rating, Avatar, FormControl, FormLabel, RadioGroup, FormControlLabel, FormGroup, Checkbox, Radio, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { imagesURL } from "../../../apiRoutes/apiBase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartPlus, faClose, faStar } from "@fortawesome/free-solid-svg-icons";
import ReadOnlyContent from "../../../components/ReadOnlyContent";
import { useTranslation } from "react-i18next";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";


import { getProductDetails } from "../../../controllers/public/PublicProductsController";
import Review from "../../../components/review";
import { Helmet } from "react-helmet-async";
import RichTextReader from "../../../components/RichTextReader";
import BookingWidget from "../../../components/bookings/BookingWidget";
import moment from "moment";
import { addItemToCart } from "../../../controllers/commerce/CartController";


function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function MerchantMenuProductsDetails(props) {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    // Desestructuramos las props
    // AHORA RECIBIMOS 'productId' y 'entityUuid'
    const { open, onClose, productData, merchant } = props;

    // --- ESTADOS INTERNOS DEL COMPONENTE ---
    const [isLoading, setIsLoading] = useState(false);
    const [product, setProduct] = useState(null); // Aquí guardaremos los datos completos
    const [selectedOptions, setSelectedOptions] = useState({});
    const [selectedModifiers, setSelectedModifiers] = useState({});

    const [currentPrice, setCurrentPrice] = useState('');
    const [openDialogReview, setOpenDialogReview] = useState(false);

    const [bookingData, setBookingData] = useState(null); // Guardará fecha/hora seleccionada
    const [quantity, setQuantity] = useState(1);

    const [value, setValue] = React.useState(0);
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    /*
    |--------------------------------------------------------------------------
    | EFECTO DE CARGA DE DATOS
    |--------------------------------------------------------------------------
    | ES - Se ejecuta cuando el modal se abre (cuando 'productId' cambia).
    |      Busca la información detallada del producto en la API.
    */
    useEffect(() => {
        // Si el modal se abre y tenemos un ID de producto...
        if (open && productData) {
            handleGetProductDetailsData(merchant.uuid, productData.id);
        }
        
        // Limpiar al cerrar
        if (!open) {
            setProduct(null);
            setSelectedOptions({});
            setCurrentPrice('');
        }
        console.log('MERCHANT ===> ', merchant);
        console.log('PRODUCT ===> ', productData);
    }, [productData]); // Dependencias del efecto

    
    const handleGetProductDetailsData = async (entityUuid, productId) => {
        const {merchant, product} = await getProductDetails(entityUuid, productId);
        setProduct(product);
        
    }

    /*
    |--------------------------------------------------------------------------
    | PROCESAR VARIANTES
    |--------------------------------------------------------------------------
    | ES - Procesa las variantes para crear los grupos de opciones.
    */
    const optionsMap = useMemo(() => {
        const options = {};
        if (!product || !product.has_variants || !product.variants) {
            return options;
        }
        product.variants.forEach(variant => {
            variant.attributes.forEach(attr => {
                if (!options[attr.name]) options[attr.name] = new Set();
                options[attr.name].add(attr.value);
            });
        });
        Object.keys(options).forEach(key => {
            options[key] = Array.from(options[key]);
        });
        return options;
    }, [product]);

    /*
    |--------------------------------------------------------------------------
    | EFECTO DE RESETEO (AL CARGAR DATOS)
    |--------------------------------------------------------------------------
    | ES - Se ejecuta cuando los datos del producto terminan de cargar.
    |      Resetea el precio y pre-selecciona las opciones.
    */
    useEffect(() => {
        if (product) {
            setCurrentPrice(product.price_from);
            if (product.has_variants) {
                const defaultOptions = {};
                Object.keys(optionsMap).forEach(optionName => {
                    if (optionsMap[optionName] && optionsMap[optionName].length > 0) {
                        defaultOptions[optionName] = optionsMap[optionName][0];
                    }
                });
                setSelectedOptions(defaultOptions);
                setSelectedModifiers({});
            } else {
                setSelectedOptions({});
            }
            
        }
    }, [product, optionsMap]);

    /*
    |--------------------------------------------------------------------------
    | EFECTO DE CÁLCULO DE PRECIO
    |--------------------------------------------------------------------------
    | ES - Se ejecuta cuando la selección del usuario cambia.
    */
    useEffect(() => {
        if (!product) return; // Si no hay producto, no hagas nada

        let basePrice = 0;
        let extrasPrice = 0;

        // 1. Calcular el precio base (de la variante seleccionada)
        if (product.has_variants) {
            if (!optionsMap || Object.keys(optionsMap).length === 0) return; // Esperar a que optionsMap esté listo

            const optionKeys = Object.keys(optionsMap);
            if (optionKeys.every(key => selectedOptions[key])) {
                const matchedVariant = product.variants.find(variant => 
                    variant.attributes.every(attr => 
                        selectedOptions[attr.name] === attr.value
                    )
                );

                if (matchedVariant) {
                    basePrice = parseFloat(matchedVariant.price);
                } else {
                    setCurrentPrice("No disponible");
                    return; // Detener si la combinación no es válida
                }
            } else {
                // Selección de variante incompleta, mostrar precio "Desde"
                setCurrentPrice(product.price_display);
                return;
            }
        } else {
            // Es un producto simple, usamos su precio base
            basePrice = parseFloat(product.variants[0].price);
        }

        // 2. Calcular el precio de los extras seleccionados
        if (product.modifier_groups && Object.keys(selectedModifiers).length > 0) {
            
            product.modifier_groups.forEach(group => {
                const selection = selectedModifiers[group.id]; // Esto es un ID (single) o un array (multiple)
                if (!selection) return; // No hay nada seleccionado en este grupo

                if (group.selection_type === 'single') {
                    const option = group.options.find(opt => opt.id === selection);
                    if (option) extrasPrice += option.price;
                } else { // 'multiple'
                    selection.forEach(optionId => {
                        const option = group.options.find(opt => opt.id === optionId);
                        if (option) extrasPrice += option.price;
                    });
                }
            });
        }

        // 3. Sumar y mostrar el precio total
        const totalPrice = basePrice + extrasPrice;
        setCurrentPrice(`${totalPrice.toFixed(2)}`);

    }, [selectedOptions, selectedModifiers, product, optionsMap]);

    /*
    |--------------------------------------------------------------------------
    | MANEJADOR DE CLIC
    |--------------------------------------------------------------------------
    */
    const handleOptionClick = (optionName, value) => {
        setSelectedOptions(prev => ({
            ...prev,
            [optionName]: value
        }));
    };

    const handleCloseReviewDialog = () => {
        setOpenDialogReview(false);
        handleGetProductDetailsData(merchant.uuid, productData.id); // Recargar detalles del producto
    }


    /*
    |--------------------------------------------------------------------------
    | MANEJADOR DE CLIC DE MODIFICADORES (SIMPLIFICADO)
    |--------------------------------------------------------------------------
    | ES - Actualiza el estado cuando el usuario selecciona o deselecciona una opción.
    */
    const handleModifierChange = (groupId, newValue) => {
        // 'newValue' ya es el valor final:
        // - Para 'single': es el ID de la opción seleccionada o 'null' si se deselecciona.
        // - Para 'multiple': es un array de los IDs seleccionados (ej. [10, 11]).
        setSelectedModifiers(prev => ({
            ...prev,
            [groupId]: newValue
        }));
    };
    


    const handleAddToCart = async () => {
        
        // 1. Definir si es reservable de forma segura
        const isReservable = product.is_reservable === 1 || product.is_reservable === true || product.is_reservable === "1";

        // 2. Validación temprana: Si es reservable y no hay datos del widget, DETENER.
        if (isReservable && !bookingData) {
            alert("Por favor selecciona un profesional y un horario.");
            return; 
        }

        

        // Variable local donde guardaremos la data procesada
        let finalBookingOptions = null;

        // 3. BLOQUE DE PROCESAMIENTO (Síncrono)
        if (isReservable) {
            try {
                console.log("⚙️ Procesando datos de reserva...");
                
                // Cálculos de fecha
                const dateStr = moment(bookingData.date).format('YYYY-MM-DD');
                const startDateTimeStr = `${dateStr} ${bookingData.time}:00`;
                const duration = product.duration_minutes || 60; 
                
                const startMoment = moment(startDateTimeStr);
                const endMoment = startMoment.clone().add(duration, 'minutes');

                // Mapeo de Recurso
                let employeeId = null;
                let assetId = null;
                
                if (bookingData.resource) {
                    const type = bookingData.resource.type; 
                    if (type === 'employee') employeeId = bookingData.resource.id;
                    else if (type === 'asset') assetId = bookingData.resource.id;
                }

                // Asignamos valor a la variable local
                finalBookingOptions = {
                    start_datetime: startMoment.format('YYYY-MM-DD HH:mm:ss'),
                    end_datetime: endMoment.format('YYYY-MM-DD HH:mm:ss'),
                    employee_id: employeeId,
                    asset_id: assetId,
                    display_date: moment(bookingData.date).format('dddd D [de] MMMM'),
                    display_time: bookingData.displayTime,
                    resource_name: bookingData.resource?.name || 'Cualquiera',
                    resource_image: bookingData.resource?.image || null,
                    duration_minutes: duration
                };
                
                console.log("✅ Datos procesados correctamente:", finalBookingOptions);

            } catch (error) {
                console.error("❌ Error calculando fechas:", error);
                
                return; // Detenemos todo si falla el cálculo
            }
        }

        // 4. GUARDIA DE SEGURIDAD FINAL
        // Si es reservable PERO finalBookingOptions sigue siendo null, algo falló arriba.
        if (isReservable && !finalBookingOptions) {
            console.error("⛔ ERROR CRÍTICO: Se intentó enviar una reserva sin datos procesados.");
            alert("Error interno procesando la reserva. Intente nuevamente.");
            
            return; // ¡AQUÍ EVITAMOS QUE SE ENVÍE EL PAYLOAD VACÍO!
        }

        // 5. CONSTRUIR PAYLOAD
        // Ahora estamos 100% seguros de que si es reservable, finalBookingOptions tiene datos.
        const payload = {
            product_id: product.id,
            quantity: isReservable ? 1 : quantity,
            type: isReservable ? 'booking' : 'marketplace',
            options: {
                variants: selectedOptions,
                modifiers: selectedModifiers,
                booking: finalBookingOptions // Aquí ya no puede ser null si es booking
            }
        };

        console.log("📦 Enviando Payload al Backend:", payload);

        // 6. LLAMADA AL BACKEND
        // El await espera a la promesa de la API, no al if anterior (que ya terminó).
        const result = await addItemToCart(payload);
        
        

        if (result.success) {
            onClose();
            alert("¡Agregado al carrito!");
        } else {
            alert(result.message);
        }
    };

    const handleSlotSelect = (data) => {
        // data trae: { date: DateObject, time: "10:00", displayTime: "10:00 AM" }
        setBookingData(data);
        console.log("Horario seleccionado:", data);
    };


    return (
        <Dialog
            fullScreen={isMobile} // <-- El modal siempre es pantalla completa
            open={open}
            onClose={onClose}
            sx={{ 
                p: { xs: 0, md: 0, lg: 4, xl: 6 },
            }}
            maxWidth='xl'
        >
            
            <Box sx={{display:'flex',flexDirection:'row',justifyContent:'end'}}>
                <IconButton aria-label="delete" onClick={onClose}>
                    <FontAwesomeIcon icon={faClose}/>
                </IconButton>
            </Box>
            

            {/* --- ESTADO DE CARGA --- */}
            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                    <CircularProgress />
                </Box>
            )}

            {/* --- ESTADO DE ERROR O VACÍO --- */}
            {!isLoading && !product && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                    <Typography>No se pudo cargar el producto.</Typography>
                </Box>
            )}

            {/* --- CONTENIDO DEL PRODUCTO (CUANDO YA CARGÓ) --- */}
            {!isLoading && product && (
                <Grid container spacing={3}>
                    <Helmet prioritizeSeoTags>
                        <title>{product.name} - QUD</title>
                        <meta name="description" content={product.descripcion} />
                    </Helmet>
                    {/**Imagen */}
                    <Grid item xs={12} sm={12} md={5} lg={4} xl={4}>
                        <Box>
                            <img
                                style={{width:'100%',borderRadius:10}}
                                src={imagesURL+'productos/'+product.image_url}
                                alt={product.name}
                            />
                        </Box>
                    </Grid>

                    {/**Datos principales */}
                    <Grid item xs={12} sm={12} md={7} lg={8} xl={8}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Typography variant="h5" fontWeight={'bold'}>{product.name}</Typography>   
                                <Typography variant="body1" component={'p'}>Categoría: <span style={{fontWeight:'bold'}}>{product.category}</span></Typography>   
                                {/*<Typography variant="body1" component={'p'}>Código: <span style={{fontWeight:'bold'}}>{product.uuid}</span></Typography>*/}
                            </Grid>
                            <Grid item xs={12} sm={2} md={4} lg={12} xl={12}>
                                <Typography variant="h4" fontWeight={'bold'} >{`$${currentPrice}`}<span style={{fontSize:15,fontWeight:'normal'}}>{`/${product.unit_symbol}`}</span></Typography>
                                
                                <Box sx={{display:'flex',flexDirection:'row',alignItems:'center'}}>
                                    <Typography variant="body1" component={'p'}>Calificación: </Typography>
                                    <Rating
                                        name="read-only"
                                        value={product.review_average || 0}
                                        readOnly
                                    />
                                    <Typography variant="body2" ml={1} component={'p'}>({product.review_count}) reseñas</Typography>
                                </Box>
                            </Grid>

                            

                            {/* --- SECCIÓN DE VARIANTES --- */}
                            {product.has_variants && Object.keys(optionsMap).map(optionName => (
                                <Grid item xs={12} key={optionName}>
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                        {optionName}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {optionsMap[optionName].map(value => (
                                            <Button
                                                key={value}
                                                variant={selectedOptions[optionName] === value ? "contained" : "outlined"}
                                                color="primary"
                                                size="small"
                                                onClick={() => handleOptionClick(optionName, value)}
                                            >
                                                {value}
                                            </Button>
                                        ))}
                                    </Box>
                                </Grid>
                            ))}
                            {/* --- FIN DE SECCIÓN DE VARIANTES --- */}



                            {/* --- SECCIÓN DE EXTRAS (MODIFICADORES) --- */}
                            {product.modifier_groups && product.modifier_groups.length > 0 && (
                                <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                        {product.modifier_groups.map((group) => (
                                            
                                            <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={group.id}>
                                                <FormControl component="fieldset" sx={{ width: '100%' }}>
                                                    
                                                    <FormLabel component="legend">{group.name} {group.required ? '*' : ''}</FormLabel>
                                                    
                                                    {group.selection_type === 'single' ? (
                                                        // RENDERIZAR COMO BOTONES ÚNICOS
                                                        <ToggleButtonGroup
                                                            orientation="vertical"
                                                            fullWidth
                                                            value={selectedModifiers[group.id] || null}
                                                            exclusive // Permite deseleccionar
                                                            onChange={(e, newValue) => handleModifierChange(group.id, newValue)}
                                                        >
                                                            {group.options && group.options.map(option => ( // <-- Chequeo de seguridad
                                                                <ToggleButton value={option.id} key={option.id} aria-label={option.name}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', textTransform: 'none' }}>
                                                                        <Typography variant="body2">{option.name}</Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                                                                            {option.price > 0 ? `(+$${option.price.toFixed(2)})` : ''}
                                                                        </Typography>
                                                                    </Box>
                                                                </ToggleButton>
                                                            ))}
                                                        </ToggleButtonGroup>
                                                    ) : (
                                                        // RENDERIZAR COMO BOTONES MÚLTIPLES
                                                        <ToggleButtonGroup
                                                            orientation="vertical"
                                                            fullWidth
                                                            value={selectedModifiers[group.id] || []}
                                                            onChange={(e, newValues) => handleModifierChange(group.id, newValues)}
                                                        >
                                                            {group.options && group.options.map(option => ( // <-- Chequeo de seguridad
                                                                <ToggleButton value={option.id} key={option.id} aria-label={option.name}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', textTransform: 'none' }}>
                                                                        <Typography variant="body2">{option.name}</Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                                                                            {option.price > 0 ? `(+$${option.price.toFixed(2)})` : ''}
                                                                        </Typography>
                                                                    </Box>
                                                                </ToggleButton>
                                                            ))}
                                                        </ToggleButtonGroup>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            )}
                            {/* --- FIN DE SECCIÓN DE EXTRAS --- */}


                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12} >
                                <Grid container spacing={3}>
                                    <Grid item>
                                        <Button sx={{display:'flex',flexDirection:'column'}}  target='_blank' href={`https://wa.me/${merchant.phone}`}>
                                            <FontAwesomeIcon size="2x" color={theme.palette.link.main} icon={faWhatsapp}/>
                                            <Typography variant="body1" color={theme.palette.link.main}>Ordenar</Typography>
                                        </Button>
                                    </Grid>

                                    {/* SECCIÓN DE RESERVA O CANTIDAD */}
                                    <Grid item xs={12}>
                                        {product.is_reservable ? (
                                            // SI ES RESERVABLE: Mostramos Calendario
                                            <BookingWidget 
                                                productId={product.id} 
                                                onSlotSelect={handleSlotSelect}
                                            />
                                        ) : (
                                            // SI ES PRODUCTO NORMAL: Selector de Cantidad
                                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                                                <Typography fontWeight="bold">Cantidad:</Typography>
                                                <Button variant="outlined" size="small" onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</Button>
                                                <Typography>{quantity}</Typography>
                                                <Button variant="outlined" size="small" onClick={() => setQuantity(q => q + 1)}>+</Button>
                                            </Box>
                                        )}
                                    </Grid>

                                    <Button 
                                        variant="contained" 
                                        color="primary"
                                        startIcon={<FontAwesomeIcon icon={faCartPlus} />}
                                        onClick={handleAddToCart}
                                        // Deshabilitar visualmente si es reserva y no han elegido hora
                                        disabled={product.is_reservable && !bookingData} 
                                        fullWidth
                                        size="large"
                                    >
                                        {product.is_reservable ? 'Reservar Cita' : 'Agregar al Carrito'}
                                        {/* Mostrar precio total */}
                                        {` - $${(parseFloat(currentPrice) * (product.is_reservable ? 1 : quantity)).toFixed(2)}`}
                                    </Button>

                                </Grid>
                            </Grid>
                            
                        </Grid>
                    </Grid>

                    {/**Descripción */}
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs value={value} onChange={handleChange} aria-label="product tabs">
                                <Tab label="Descripción" {...a11yProps(0)} />
                                <Tab label="Reseñas" {...a11yProps(1)} />
                                </Tabs>
                            </Box>
                            <CustomTabPanel value={value} index={0}>
                                <RichTextReader content={product.description} />
                            </CustomTabPanel>
                            <CustomTabPanel value={value} index={1}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <Button variant="contained" onClick={()=>setOpenDialogReview(true)}>Deja una Reseña</Button>
                                        <Review
                                            open={openDialogReview}
                                            type={'product'}
                                            toUUID={product.uuid}
                                            handleClose={()=>handleCloseReviewDialog()}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                        {
                                            product.reviews && product.reviews.length > 0
                                            ? product.reviews.map(review=>(
                                                <Box key={review.id} sx={{mb:2,p:2,border:'1px solid #ccc',borderRadius:2}}>
                                                    <Grid container spacing={2}>
                                                        
                                                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12} display={'flex'} flexDirection={'row'} alignItems={'center'}>
                                                            <Avatar>{review.author[0].toUpperCase()}</Avatar>
                                                            <Box sx={{ml:2}}>
                                                                <Typography variant="body1" fontWeight={'bold'}>{review.author}</Typography>
                                                                <Box sx={{display:'flex',flexDirection:'row',alignItems:'center'}}>
                                                                    <Rating
                                                                        name="read-only"
                                                                        value={review.score}
                                                                        readOnly
                                                                        
                                                                    />
                                                                    <Typography variant="body2" ml={1}>{review.score} estrellas</Typography>
                                                                </Box>
                                                                <Typography variant="body2">{review.created_at}</Typography>
                                                            </Box>
                                                            
                                                        </Grid>
                                                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                                            <Typography variant="body2">{review.comment}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            ))
                                            :<Typography variant="body2">Aún no hay reseñas para este producto.</Typography>
                                        }
                                    </Grid>
                                </Grid>
                            </CustomTabPanel>
                        </Box>
                        
                        
                    </Grid>
                </Grid>
            )}
        </Dialog>
    )
}